// CRITICAL HOTFIX: Aggressive cache and service worker killer
// This runs BEFORE any imports to prevent old cached bundles from executing
if (typeof window !== 'undefined') {
  // Global runtime error logging
  window.addEventListener("error", (e) => {
    console.error("[GlobalError]", e.message, e.filename, e.lineno, e.colno, e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e: any) => {
    console.error("[GlobalPromiseRejection]", e?.reason || e);
  });

  // Kill all service workers immediately
  if ('serviceWorker' in navigator) {
    try {
      navigator.serviceWorker.getRegistrations().then(rs => {
        rs.forEach(r => r.unregister());
        console.log('[Cache] Service workers unregistered');
      });
    } catch (e) {
      console.warn('[Cache] SW cleanup failed:', e);
    }
  }

  // Clear all caches
  if ('caches' in window) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).caches?.keys?.().then((keys: string[]) => {
        keys.forEach(k => caches.delete(k));
        console.log('[Cache] All caches cleared');
      });
    } catch (e) {
      console.warn('[Cache] Cache cleanup failed:', e);
    }
  }
}

import React from 'react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { initializeMonitoring } from './lib/monitoring-config';
import { ThemeProvider } from './components/theme-provider';
import { RootBoundary } from './components/system/RootBoundary';
import { queryClient } from './lib/queryClient';
import App from './App.tsx';
import './index.css';

// DayPicker base styles
import 'react-day-picker/dist/style.css';
// Our alignment overrides (must come AFTER the base CSS)
import './styles/daypicker-fixes.css';

// Initialize error monitoring (Sentry + BrowserTracing + Replay)
initializeMonitoring();

// Build stamp for deployment verification
 
console.info("[App] BUILD_ID:", (typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev"));

// Check for build version changes and update if needed
// TEMPORARY: Disabled due to file sync issues
// checkBuildVersion();

// Tenant context must come from auth + profile / TenantProvider — never from URL → localStorage.

// Development guards
if (import.meta.env.DEV) {
  import('./dev/guard-native-date');
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootBoundary>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <ThemeProvider>
            <App />
            <Toaster />
          </ThemeProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </RootBoundary>
  </StrictMode>
);
